import { Component, OnInit, OnDestroy, Input, ChangeDetectorRef } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd, RouterLink } from '@angular/router';
import { BreadcrumbsService, IBreadcrumb } from './breadcrumbs.service';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateDirective } from 'cs-shared-lib';

@Component({
  selector: 'app-breadcrumbs',
  templateUrl: './breadcrumbs.html',
  styleUrl: './breadcrumbs.scss',
  imports: [RouterLink, TranslateDirective],
})
export class BreadcrumbsComponent implements OnInit, OnDestroy {
  @Input() set breadcrumbs(value: any[]) {
    if (value) {
      this.isManual = true;
      this._breadcrumbs = value;
    }
  }

  get breadcrumbs(): any[] {
    return this._breadcrumbs;
  }

  private _breadcrumbs: any[] = [];
  public isManual: boolean = false;
  dynamicLabel: string | null = null;
  private destroyed = false;

  ngOnDestroy(): void {
    this.destroyed = true;
  }

  private safeDetectChanges(): void {
    if (!this.destroyed) {
      Promise.resolve().then(() => {
        if (!this.destroyed) {
          this.cdr.detectChanges();
        }
      });
    }
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private breadcrumbsService: BreadcrumbsService,
    private cdr: ChangeDetectorRef,
  ) {
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        if (!this.isManual) {
          this._breadcrumbs = this.buildBreadcrumb(this.route.root);
          this.safeDetectChanges();
        }
      });

    this.breadcrumbsService.breadcrumb$.pipe(takeUntilDestroyed()).subscribe((label) => {
      this.dynamicLabel = label;
      if (this.dynamicLabel && this._breadcrumbs.length) {
        this._breadcrumbs[this._breadcrumbs.length - 1].label = label;
        this.safeDetectChanges();
      }
    });

    this.breadcrumbsService.breadcrumbs$.pipe(takeUntilDestroyed()).subscribe((breadcrumbs) => {
      if (breadcrumbs !== null) {
        this.breadcrumbs = breadcrumbs;
      } else {
        this._breadcrumbs = [];
        this.isManual = false;
      }
      this.safeDetectChanges();
    });
  }

  ngOnInit(): void {
    if (!this.isManual) {
      this._breadcrumbs = this.buildBreadcrumb(this.route.root);
    }
  }

  buildBreadcrumb(
    route: ActivatedRoute | null,
    url: string = '',
    breadcrumbs: IBreadcrumb[] = [],
  ): IBreadcrumb[] {
    if (!route) {
      return breadcrumbs;
    }
    const children = route.children;

    if (children.length === 0) {
      return breadcrumbs;
    }

    for (const child of children) {
      if (!child || !child.snapshot) {
        continue;
      }

      const routeURL = child.snapshot.url.map((segment) => segment.path).join('/');

      if (routeURL) {
        url += `/${routeURL}`;
      }

      const label = child.snapshot.data['breadcrumb'];

      if (label) {
        breadcrumbs.push({
          label,
          url,
        });
      }

      return this.buildBreadcrumb(child, url, breadcrumbs);
    }

    return breadcrumbs;
  }

  onNavigate(event: Event, breadcrumb: IBreadcrumb) {
    const mouseEvent = event as MouseEvent;

    // Allow standard browser behaviors (Ctrl+Click, Cmd+Click, Shift+Click, Middle-click)
    // to open in a new tab or window by returning early.
    if (
      mouseEvent.ctrlKey ||
      mouseEvent.metaKey ||
      mouseEvent.shiftKey ||
      mouseEvent.button === 1
    ) {
      return;
    }

    if (this.isManual) {
      event.preventDefault();

      if (breadcrumb.url) {
        // Host app (angularES) handles hybrid routing via mfe-external-navigate
        // clearBreadcrumbs() is intentionally NOT called here — it caused the
        // breadcrumb bar to flash empty before the page actually navigated.
        // The host's onMfeExternalNavigate clears breadcrumbs right before routing.
        window.dispatchEvent(
          new CustomEvent('mfe-external-navigate', {
            detail: { url: breadcrumb.url },
          }),
        );
      }
    }
  }
}
// }
